import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';

import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Add, Visibility } from '@material-ui/icons';
import ReactSelect from 'react-select';

import { useKeycloak } from '@react-keycloak/web';

import { useStyles, StyledTableCell } from './styles';
import {
  getStatusChip,
  getPriorityChip,
  getCategoryChip,
  getOverdueChip,
  isTicketOverdue,
  statusColors,
} from '../chips';

import { useAxios } from '../../../utils/hooks';
import Loader from '../../../components/Loader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import {
  Ticket,
  TicketCategory,
  TicketCategoryLabels,
  TicketPriority,
  TicketPriorityLabels,
  TicketStatus,
  TicketStatusLabels,
} from '../../../models/ticket';

const summaryStatuses: TicketStatus[] = [
  TicketStatus.OPEN,
  TicketStatus.IN_PROGRESS,
  TicketStatus.RESOLVED,
  TicketStatus.WAITING,
  TicketStatus.CLOSED,
  TicketStatus.CANCELLED,
];

const primaryStatuses: TicketStatus[] = [
  TicketStatus.OPEN,
  TicketStatus.IN_PROGRESS,
  TicketStatus.RESOLVED,
];

const priorityOptions = Object.values(TicketPriority).map((value) => ({
  value,
  label: TicketPriorityLabels[value],
}));

const categoryOptions = Object.values(TicketCategory).map((value) => ({
  value,
  label: TicketCategoryLabels[value],
}));

type LocationState = {
  alert?: {
    type: AlertComponentProps['type'];
    message: string;
  };
};

const HelpdeskList: React.FC = () => {
  const api = useAxios();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { keycloak } = useKeycloak();

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(4);
  const [onlyMine, setOnlyMine] = useState(false);
  const [recordNumber, setRecordNumber] = useState('');
  const [activeStatus, setActiveStatus] = useState<TicketStatus | 'all'>('all');
  const [filterTypes, setFilterTypes] = useState({
    priority: '',
    category: '',
    recordNumbers: '',
    author_id: '',
  });

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  const filters = `${filterTypes.priority}${filterTypes.category}${filterTypes.recordNumbers}${filterTypes.author_id}`;

  useEffect(() => {
    async function getTickets() {
      setLoading(true);

      try {
        const ticketsResponse = await api.current?.get(`/helpdesk?${filters}`);

        setTickets(ticketsResponse?.data || []);
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao buscar tickets.',
        });
      } finally {
        setLoading(false);
      }
    }

    getTickets();
  }, [api, filters]);

  useEffect(() => {
    if (location.state?.alert) {
      setAlert({ isActive: true, ...location.state.alert });
      history.replace(location.pathname, {});
    }
  }, [location, history]);

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleFilter = useCallback((type: string, data: any) => {
    switch (type) {
      case 'priority':
        setFilterTypes((prev) => ({
          ...prev,
          priority: data && data.value ? `&priority=${data.value}` : '',
        }));
        break;
      case 'category':
        setFilterTypes((prev) => ({
          ...prev,
          category: data && data.value ? `&category=${data.value}` : '',
        }));
        break;
      default:
        break;
    }
  }, []);

  const handleRecordNumberFilter = useCallback(() => {
    setFilterTypes((prev) => ({
      ...prev,
      recordNumbers: recordNumber ? `&recordNumbers=${recordNumber}` : '',
    }));
  }, [recordNumber]);

  const handleToggleMine = useCallback(
    (checked: boolean) => {
      setOnlyMine(checked);
      setFilterTypes((prev) => ({
        ...prev,
        author_id:
          checked && keycloak?.subject ? `&author_id=${keycloak.subject}` : '',
      }));
    },
    [keycloak],
  );

  const statusCounts = useMemo(() => {
    const counts = summaryStatuses.reduce(
      (acc, status) => ({ ...acc, [status]: 0 }),
      {} as { [key in TicketStatus]: number },
    );

    tickets.forEach((ticket) => {
      if (counts[ticket.status] != null) {
        counts[ticket.status] += 1;
      }
    });

    return counts;
  }, [tickets]);

  const filteredTickets = useMemo(
    () =>
      activeStatus === 'all'
        ? tickets
        : tickets.filter((ticket) => ticket.status === activeStatus),
    [tickets, activeStatus],
  );

  const handleSelectStatus = useCallback((status: TicketStatus | 'all') => {
    setActiveStatus((prev) => (prev === status ? 'all' : status));
    setPage(0);
  }, []);

  const labelDisplayedRows = ({
    count,
    from,
    to,
  }: {
    count: number;
    from: number;
    to: number;
  }) => `${from} - ${to} de ${count}`;

  const mineSwitch = (
    <Switch
      checked={onlyMine}
      color="primary"
      onChange={(event) => handleToggleMine(event.target.checked)}
    />
  );

  return (
    <>
      <Loader loading={loading} />

      <div className={classes.header}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          className={classes.addButton}
          onClick={() => history.push('/helpdesk/novo')}
        >
          Novo Ticket
        </Button>
      </div>

      <Paper elevation={0} className={classes.filterCard}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4}>
            <span className={classes.filterLabel}>Prioridade</span>
            <ReactSelect
              placeholder="Prioridade"
              isClearable
              options={priorityOptions}
              onChange={(data: any) => handleFilter('priority', data)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <span className={classes.filterLabel}>Categoria</span>
            <ReactSelect
              placeholder="Categoria"
              isClearable
              options={categoryOptions}
              onChange={(data: any) => handleFilter('category', data)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <span className={classes.filterLabel}>Nº Prontuário</span>
            <TextField
              type="number"
              size="small"
              fullWidth
              value={recordNumber}
              onChange={(event) => setRecordNumber(event.target.value)}
              onBlur={handleRecordNumberFilter}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleRecordNumberFilter();
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              className={classes.toggleField}
              control={mineSwitch}
              label="Meus tickets"
            />
          </Grid>
        </Grid>
      </Paper>

      <div className={classes.statusFilterBar}>
        <button
          type="button"
          className={`${classes.statusPill} ${classes.statusPillAll} ${
            activeStatus === 'all' ? classes.statusPillActive : ''
          }`}
          onClick={() => handleSelectStatus('all')}
        >
          <span className={classes.statusPillLabel}>Todos</span>
          <span className={classes.statusPillCount}>{tickets.length}</span>
        </button>

        {summaryStatuses.map((status) => {
          const isPrimary = primaryStatuses.includes(status);

          return (
            <button
              type="button"
              key={status}
              className={`${classes.statusPill} ${
                isPrimary
                  ? classes.statusPillPrimary
                  : classes.statusPillSecondary
              } ${activeStatus === status ? classes.statusPillActive : ''}`}
              style={
                activeStatus === status
                  ? {
                      borderColor: statusColors[status],
                      background: statusColors[status],
                    }
                  : { borderColor: `${statusColors[status]}55` }
              }
              onClick={() => handleSelectStatus(status)}
            >
              <span
                className={classes.statusPillDot}
                style={{ background: statusColors[status] }}
              />
              <span className={classes.statusPillLabel}>
                {TicketStatusLabels[status]}
              </span>
              <span
                className={classes.statusPillCount}
                style={
                  activeStatus === status
                    ? undefined
                    : { color: statusColors[status] }
                }
              >
                {statusCounts[status]}
              </span>
            </button>
          );
        })}
      </div>

      <TableContainer component={Paper} style={{ marginBottom: '2%' }}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Título</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Prioridade</StyledTableCell>
              <StyledTableCell>Categoria</StyledTableCell>
              <StyledTableCell>Prontuários</StyledTableCell>
              <StyledTableCell>Prazo</StyledTableCell>
              <StyledTableCell>Horas</StyledTableCell>
              <StyledTableCell>Criado em</StyledTableCell>
              <StyledTableCell align="center">Visualizar</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!filteredTickets.length && (
              <TableRow>
                <TableCell component="th" scope="row" colSpan={9}>
                  <Typography>Nenhum ticket encontrado.</Typography>
                </TableCell>
              </TableRow>
            )}

            {(rowsPerPage > 0
              ? filteredTickets.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : filteredTickets
            ).map((ticket) => (
              <TableRow
                key={ticket._id}
                hover
                style={{ cursor: 'pointer' }}
                onClick={() => history.push(`/helpdesk/${ticket._id}`)}
              >
                <TableCell component="th" scope="row">
                  {ticket.title}
                </TableCell>

                <TableCell>{getStatusChip(ticket.status)}</TableCell>

                <TableCell>{getPriorityChip(ticket.priority)}</TableCell>

                <TableCell>{getCategoryChip(ticket.category)}</TableCell>

                <TableCell>
                  {ticket.recordNumbers && ticket.recordNumbers.length
                    ? ticket.recordNumbers.join(', ')
                    : '--'}
                </TableCell>

                <TableCell>
                  {ticket.dueDate ? (
                    <Grid container spacing={1} alignItems="center">
                      <Grid item>
                        {moment(ticket.dueDate).format('DD/MM/YYYY')}
                      </Grid>
                      {isTicketOverdue(ticket) && (
                        <Grid item>{getOverdueChip()}</Grid>
                      )}
                    </Grid>
                  ) : (
                    '--'
                  )}
                </TableCell>

                <TableCell>
                  {ticket.hoursSpent != null ? ticket.hoursSpent : '--'}
                </TableCell>

                <TableCell>
                  {moment(ticket.createdAt).format('DD/MM/YYYY HH:mm')}
                </TableCell>

                <TableCell align="center">
                  <Tooltip title="Visualizar">
                    <IconButton
                      color="primary"
                      onClick={(event) => {
                        event.stopPropagation();
                        history.push(`/helpdesk/${ticket._id}`);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 100]}
          component="div"
          count={filteredTickets.length}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage={<span>Tickets por página:</span>}
          labelDisplayedRows={labelDisplayedRows}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </TableContainer>

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default HelpdeskList;
